import { useState, useEffect } from "react";
import { Mail, Heart, Sparkles, FileText, Check, Edit3, Smile, ScrollText } from "lucide-react";

/**
 * Animated full-flow demo — viser kerne-værdien:
 * 1. Bekræft email
 * 2. Vælg anledning + tone
 * 3. Udfyld detaljer (navne, alder, anekdote)
 * 4. AI skriver 2 udkast
 * 5. Sammenlign 2 udkast
 * 6. Redigér / kombinér
 * 7. Færdig tale på flot brevpapir
 */

const TYPED_EMAIL = "anna@gmail.com";
const TYPED_NAMES = "Anne & Thomas";
const TYPED_ANECDOTE =
  "Vi mødtes til en koncert. Anne trådte Thomas over tæerne og undskyldte med en øl.";

const DRAFT_1_PREVIEW = [
  "Kære Anne og Thomas,",
  "Jeg husker stadig den koncertaften.",
  "Anne, du trådte Thomas over tæerne...",
];
const DRAFT_2_PREVIEW = [
  "Kære brudepar,",
  "Det startede med en koncert og en øl.",
  "Anne trådte — Thomas faldt for hende...",
];

const FINAL_SPEECH = [
  "Kære Anne og Thomas,",
  "",
  "Det startede med en koncert og et trådt på tæerne.",
  "Anne, du gav Thomas en øl som undskyldning — og det",
  "er nok den bedste handel, han nogensinde har lavet.",
  "",
  "I dag står I her. Og I beviser at de bedste",
  "kærlighedshistorier starter med små uheld.",
  "",
  "Skål for jer to!",
];

const TONES = [
  { label: "Sjov", icon: "😄", active: true },
  { label: "Rørende", icon: "❤️", active: true },
  { label: "Formel", icon: "🎩", active: false },
  { label: "Hjertelig", icon: "🤗", active: false },
];

const OCCASIONS = ["Bryllup", "Konfirmation", "Fødselsdag", "Studenter"];

export default function AnimatedFlowDemo() {
  // Phases: 0=email, 1=occasion+tone, 2=details, 3=AI, 4=two-drafts, 5=editing, 6=final
  const [phase, setPhase] = useState(0);
  const [typedEmail, setTypedEmail] = useState("");
  const [typedNames, setTypedNames] = useState("");
  const [typedAnecdote, setTypedAnecdote] = useState("");
  const [visibleLines, setVisibleLines] = useState(0);

  useEffect(() => {
    const timers = [];

    const runCycle = () => {
      setTypedEmail("");
      setTypedNames("");
      setTypedAnecdote("");
      setVisibleLines(0);
      setPhase(0);

      // Phase 0: type email (0 - 2s)
      let i = 0;
      const e = setInterval(() => {
        i++;
        setTypedEmail(TYPED_EMAIL.slice(0, i));
        if (i >= TYPED_EMAIL.length) clearInterval(e);
      }, 90);
      timers.push(e);

      // Phase 1: occasion + tone (at 2.5s)
      timers.push(setTimeout(() => setPhase(1), 2500));

      // Phase 2: fill details (at 5s)
      timers.push(
        setTimeout(() => {
          setPhase(2);
          // Type names
          let n = 0;
          const ni = setInterval(() => {
            n++;
            setTypedNames(TYPED_NAMES.slice(0, n));
            if (n >= TYPED_NAMES.length) clearInterval(ni);
          }, 70);
          timers.push(ni);
          // Then type anecdote 1s later
          timers.push(
            setTimeout(() => {
              let a = 0;
              const ai = setInterval(() => {
                a++;
                setTypedAnecdote(TYPED_ANECDOTE.slice(0, a));
                if (a >= TYPED_ANECDOTE.length) clearInterval(ai);
              }, 28);
              timers.push(ai);
            }, 1300)
          );
        }, 5000)
      );

      // Phase 3: AI thinking (at 9s)
      timers.push(setTimeout(() => setPhase(3), 9000));

      // Phase 4: 2 drafts shown (at 11s)
      timers.push(setTimeout(() => setPhase(4), 11000));

      // Phase 5: editing (at 14s)
      timers.push(setTimeout(() => setPhase(5), 14000));

      // Phase 6: final printed (at 16.5s)
      timers.push(
        setTimeout(() => {
          setPhase(6);
          let k = 0;
          const li = setInterval(() => {
            k++;
            setVisibleLines(k);
            if (k >= FINAL_SPEECH.length) clearInterval(li);
          }, 280);
          timers.push(li);
        }, 16500)
      );
    };

    runCycle();
    const loop = setInterval(runCycle, 22000); // 22s per full cycle
    timers.push(loop);

    return () => timers.forEach((t) => clearInterval(t) || clearTimeout(t));
  }, []);

  const stepLabel = [
    "1. Bekræft email",
    "2. Vælg anledning og tone",
    "3. Fortæl om personen",
    "4. AI skriver 2 udkast",
    "5. Sammenlign 2 udkast",
    "6. Redigér og kombinér",
    "7. Din færdige tale",
  ][phase];

  return (
    <section className="py-12 md:py-20 bg-background" data-testid="animated-flow-demo">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 md:mb-14">
          <span className="text-secondary font-medium text-sm uppercase tracking-wider">
            Sådan virker det
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-bold text-primary mt-2">
            Fra tomt ark til færdig tale på 2 minutter
          </h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto text-sm md:text-base">
            Se hele flowet — fra du indtaster dine oplysninger til du holder en flot tale i hånden
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-2 bg-secondary/10 border border-secondary/30 rounded-full px-4 py-2 text-sm font-medium text-primary">
            <Sparkles className="h-4 w-4 text-secondary" />
            <span data-testid="demo-step-label">{stepLabel}</span>
          </div>
        </div>

        {/* Demo frame */}
        <div className="grid lg:grid-cols-2 gap-6 md:gap-8 items-stretch">
          {/* LEFT panel */}
          <div className="bg-white rounded-2xl shadow-xl border border-border/40 p-5 md:p-7 min-h-[460px] flex flex-col">
            {/* Browser chrome */}
            <div className="flex items-center gap-2 mb-5 pb-3 border-b border-border/40">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
              <span className="ml-2 text-xs text-muted-foreground font-mono">
                minfesttale.dk/tale
              </span>
            </div>

            {/* Phase 0: Email */}
            {phase === 0 && (
              <div className="space-y-4 animate-fade-in flex-1">
                <div className="flex items-center gap-2 text-primary">
                  <Mail className="h-5 w-5 text-secondary" />
                  <span className="font-medium">Bekræft din email</span>
                </div>
                <div className="bg-muted/40 border border-border rounded-lg px-4 py-3 text-primary font-mono">
                  {typedEmail}
                  <span className="inline-block w-0.5 h-5 bg-primary ml-0.5 animate-pulse" />
                </div>
                <p className="text-xs text-muted-foreground">
                  Vi sender en 6-cifret kode til din mail
                </p>
              </div>
            )}

            {/* Phase 1: Occasion + Tone */}
            {phase === 1 && (
              <div className="space-y-5 animate-fade-in flex-1">
                <div>
                  <div className="flex items-center gap-2 text-primary mb-2">
                    <Heart className="h-5 w-5 text-secondary" />
                    <span className="font-medium">Anledning</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {OCCASIONS.map((o, i) => (
                      <div
                        key={o}
                        className={`px-3 py-2 rounded-lg border text-center text-sm font-medium transition-all ${
                          i === 0
                            ? "bg-secondary/20 border-secondary text-primary shadow scale-105"
                            : "bg-muted/30 border-border text-muted-foreground"
                        }`}
                      >
                        {o}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-primary mb-2">
                    <Smile className="h-5 w-5 text-secondary" />
                    <span className="font-medium">Tone & stemning</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {TONES.map((t) => (
                      <div
                        key={t.label}
                        className={`px-3 py-2 rounded-lg border flex items-center gap-2 text-sm font-medium transition-all ${
                          t.active
                            ? "bg-secondary/20 border-secondary text-primary shadow"
                            : "bg-muted/30 border-border text-muted-foreground"
                        }`}
                      >
                        <span>{t.icon}</span>
                        <span>{t.label}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Kombinér flere toner for nuanceret resultat
                  </p>
                </div>
              </div>
            )}

            {/* Phase 2: Details */}
            {phase === 2 && (
              <div className="space-y-4 animate-fade-in flex-1">
                <div className="flex items-center gap-2 text-primary">
                  <ScrollText className="h-5 w-5 text-secondary" />
                  <span className="font-medium">Fortæl om personen</span>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Talen er til</p>
                  <div className="bg-muted/40 border border-border rounded-lg px-3 py-2 text-primary text-sm min-h-[40px]">
                    {typedNames}
                    {typedNames.length < TYPED_NAMES.length && (
                      <span className="inline-block w-0.5 h-4 bg-primary ml-0.5 animate-pulse" />
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">
                    Hvordan mødte de hinanden?
                  </p>
                  <div className="bg-muted/40 border border-border rounded-lg px-3 py-2 text-primary text-sm leading-relaxed min-h-[80px]">
                    {typedAnecdote}
                    {typedAnecdote.length > 0 && typedAnecdote.length < TYPED_ANECDOTE.length && (
                      <span className="inline-block w-0.5 h-4 bg-primary ml-0.5 animate-pulse" />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Phase 3: AI thinking */}
            {phase === 3 && (
              <div className="flex-1 flex flex-col items-center justify-center text-center animate-fade-in space-y-4">
                <div className="relative">
                  <div className="w-16 h-16 rounded-full border-4 border-secondary/20 border-t-secondary animate-spin" />
                  <Sparkles className="h-6 w-6 text-secondary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                </div>
                <div>
                  <p className="font-heading text-lg text-primary font-bold">
                    AI skriver 2 unikke udkast…
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Så du kan vælge eller kombinere det bedste
                  </p>
                </div>
              </div>
            )}

            {/* Phase 4: Two drafts */}
            {phase === 4 && (
              <div className="space-y-3 animate-fade-in flex-1">
                <div className="flex items-center gap-2 text-primary mb-1">
                  <FileText className="h-5 w-5 text-secondary" />
                  <span className="font-medium">2 udkast — sammenlign</span>
                </div>
                {/* Draft 1 */}
                <div className="border-2 border-secondary bg-secondary/10 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-primary">UDKAST 1</span>
                    <span className="text-xs text-secondary font-medium">Personlig</span>
                  </div>
                  <div className="font-serif text-xs text-primary/80 italic leading-relaxed">
                    {DRAFT_1_PREVIEW.map((l, i) => (
                      <p key={i}>{l}</p>
                    ))}
                  </div>
                </div>
                {/* Draft 2 */}
                <div className="border-2 border-border bg-muted/20 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-primary">UDKAST 2</span>
                    <span className="text-xs text-muted-foreground font-medium">Poetisk</span>
                  </div>
                  <div className="font-serif text-xs text-primary/80 italic leading-relaxed">
                    {DRAFT_2_PREVIEW.map((l, i) => (
                      <p key={i}>{l}</p>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Phase 5: Editing */}
            {phase === 5 && (
              <div className="space-y-3 animate-fade-in flex-1">
                <div className="flex items-center gap-2 text-primary">
                  <Edit3 className="h-5 w-5 text-secondary" />
                  <span className="font-medium">Redigér og kombinér</span>
                </div>
                <div className="bg-white border border-border rounded-lg p-3 font-serif text-sm leading-relaxed text-primary">
                  <p>Kære Anne og Thomas,</p>
                  <p>
                    Det startede med{" "}
                    <span className="bg-secondary/30 px-0.5 rounded">en koncert</span>{" "}
                    og <span className="bg-accent/30 px-0.5 rounded">en øl</span>.
                  </p>
                  <p>
                    Anne trådte — Thomas faldt for hende
                    <span className="inline-block w-0.5 h-4 bg-primary ml-0.5 animate-pulse" />
                  </p>
                </div>
                <div className="flex items-center gap-2 text-xs text-accent font-medium">
                  <Check className="h-4 w-4" />
                  <span>Tag det bedste fra begge udkast</span>
                </div>
              </div>
            )}

            {/* Phase 6: Done state on form side */}
            {phase === 6 && (
              <div className="flex-1 flex flex-col items-center justify-center text-center animate-fade-in space-y-3">
                <div className="w-16 h-16 rounded-full bg-accent/15 flex items-center justify-center">
                  <Check className="h-8 w-8 text-accent" />
                </div>
                <p className="font-heading text-lg text-primary font-bold">
                  Færdig — klar til print
                </p>
                <p className="text-xs text-muted-foreground max-w-[240px]">
                  Download som flot PDF eller udskriv direkte
                </p>
              </div>
            )}
          </div>

          {/* RIGHT panel — speech preview */}
          <div className="bg-[#fefaf3] rounded-2xl shadow-xl border border-secondary/20 p-6 md:p-10 min-h-[460px] relative overflow-hidden">
            {/* Letterhead */}
            <div className="text-center pb-4 mb-5 border-b border-secondary/30">
              <p className="text-xs uppercase tracking-widest text-secondary font-medium">
                MinFesttale
              </p>
              <p className="font-heading text-xl md:text-2xl text-primary font-bold mt-1">
                Bryllupstale
              </p>
              <p className="font-serif italic text-secondary text-sm mt-1">
                Til Anne &amp; Thomas
              </p>
            </div>

            {/* Speech content */}
            <div className="space-y-1 font-serif text-primary text-sm md:text-base leading-relaxed">
              {phase < 6 ? (
                <div className="text-muted-foreground/60 italic text-center mt-12">
                  {phase < 3
                    ? "Din personlige tale dukker op her…"
                    : phase === 3
                    ? "AI arbejder…"
                    : "Tilpas og kombinér i venstre side"}
                </div>
              ) : (
                FINAL_SPEECH.slice(0, visibleLines).map((line, i) => (
                  <p key={i} className="animate-fade-in min-h-[1.25rem]">
                    {line || "\u00A0"}
                  </p>
                ))
              )}
            </div>

            {/* "Print-ready" badge when done */}
            {phase === 6 && visibleLines >= FINAL_SPEECH.length && (
              <div className="absolute top-4 right-4 bg-accent/15 text-accent text-xs font-bold px-3 py-1 rounded-full animate-fade-in">
                ✓ Klar til print
              </div>
            )}

            <div className="absolute bottom-4 right-4 opacity-30">
              <Sparkles className="h-6 w-6 text-secondary" />
            </div>
          </div>
        </div>

        {/* Sub-CTA */}
        <div className="text-center mt-10 md:mt-12">
          <p className="text-muted-foreground text-sm md:text-base mb-4">
            Klar til at lave din egen? Det tager kun 2 minutter.
          </p>
          <a
            href="/tale"
            className="inline-flex items-center gap-2 bg-secondary hover:bg-secondary/90 text-primary font-medium px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all"
            data-testid="demo-cta-tale"
          >
            <FileText className="h-5 w-5" />
            Start din tale nu
          </a>
        </div>
      </div>
    </section>
  );
}
